// src/api/v3/orders-api-v3.js
import { authHeaders } from './_auth';
const API_BASE = process.env.REACT_APP_API_BASE;

// ---------- small helpers ----------
function unwrap(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.items && Array.isArray(payload.items)) return payload.items;
  if (payload.data && Array.isArray(payload.data)) return payload.data;
  return payload;
}

async function handleRes(res) {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = await res.json();
      msg = json?.errors?.join(', ') || json?.error || msg;
    } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

async function toJson(res) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.error || body.errors?.join(', ') || res.statusText;
    throw new Error(msg);
  }
  return res.json();
}

function setIf(u, key, val) {
  if (val === undefined || val === null || val === '') return;
  u.searchParams.set(key, String(val));
}

// ---------- Export URL builders ----------
/**
 * Build an export URL.
 * @param {'csv'|'xlsx'} fmt
 * @param {{ level?: 'items'|'orders', q?: string, status?: string, voucher_code?: string, ids?: string|string[] }} params
 */
function buildExportURL(fmt = 'csv', params = {}) {
  const u = new URL(`${API_BASE}/api/v3/orders/export.${fmt}`);
  // default to per-item export for your new list
  setIf(u, 'level', params.level || 'items');
  setIf(u, 'q', params.q);
  setIf(u, 'status', params.status);
  setIf(u, 'voucher_code', params.voucher_code);
  setIf(u, 'paid_full_only', params.paid_full_only);

  // vendor filter for export as well (CSV of keys)
  if (params.vendor) {
    const vs = Array.isArray(params.vendor) ? params.vendor : String(params.vendor).split(',');
    u.searchParams.set('vendor', vs.map(s => String(s).trim()).filter(Boolean).join(','));
  }

  // optional explicit IDs (comma-separated) for selective export
  if (params.ids) {
    const ids = Array.isArray(params.ids) ? params.ids : String(params.ids).split(',');
    setIf(u, 'ids', ids.map((s) => String(s).trim()).filter(Boolean).join(','));
  }
  return u.toString();
}

// near other imports/utilities
const asDownload = (blob, filename) => {
  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(a.href);
};

const OrdersAPI = {
  /**
   * List orders. When include_items=1, backend should embed order_items in each order.
   * Supported params: { include_items, status, q, page, per, voucher_code, vendor, paid_full_only }
   */
  async list(params = {}) {
    const url = new URL(`${API_BASE}/api/v3/orders`);
    const page = Number(params.page) > 0 ? Number(params.page) : 1;
    const per  = Number(params.per) > 0 ? Number(params.per) : 25;

    if (params.include_items) url.searchParams.set('include_items', '1');

    setIf(url, 'status', params.status);
    setIf(url, 'q', params.q);
    setIf(url, 'voucher_code', params.voucher_code);
    setIf(url, 'page', page);
    setIf(url, 'per', per);
    setIf(url, 'active_only', params.active_only);
    setIf(url, 'paid_full_only', params.paid_full_only);
    setIf(url, 'has_voucher_only', params.has_voucher_only);

    // ---- NEW: vendor filter ----
    if (params.vendor != null) {
      const vs = Array.isArray(params.vendor) ? params.vendor : String(params.vendor).split(',');
      // Backend accepts either vendor[]=... or vendor=csv — we send CSV:
      url.searchParams.set('vendor', vs.map(s => String(s).trim()).filter(Boolean).join(','));
      // If you prefer repeated keys instead, uncomment:
      // vs.forEach(v => url.searchParams.append('vendor[]', String(v)));
    }

    const res = await fetch(url.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    return handleRes(res);
  },

  async show(id) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}`, {
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    return handleRes(res);
  },

  create(order, extras = {}) {
    const body = JSON.stringify({ order, ...extras });
    return fetch(`${API_BASE}/api/v3/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders() },
      body,
    }).then(toJson);
  },

  async update(id, data) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders() },
      body: JSON.stringify({ order: data }),
    });
    return handleRes(res);
  },

  async destroy(id) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    if (!res.ok) throw new Error(`Failed to delete order #${id}`);
    return true;
  },

  async qcCheck(id, payload = {}) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}/qc_check`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    return handleRes(res);
  },

  /**
   * Request merged customer receipt PDF.
   * Throws Error(message) on 4xx/5xx (including our 422 validation).
   * Returns { blob, filename } on success.
   */
  async mergedCustomerReceipts(ids = [], opts = {}) {
    const u = new URL(`${API_BASE}/api/v3/orders/merged_customer_receipts`);
    u.searchParams.set('ids', (Array.isArray(ids) ? ids : String(ids).split(',')).map(s => String(s).trim()).filter(Boolean).join(','));
    if (opts.dl) u.searchParams.set('dl', '1');

    const res = await fetch(u.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/pdf', ...authHeaders() }
    });

    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.error || (j?.errors && j.errors.join(', ')) || msg;
      } catch (_) { /* response wasn't JSON */ }
      throw new Error(msg);
    }

    const blob = await res.blob();
    const filename = `Merged_Customer_Receipts.pdf`;
    return { blob, filename };
  },

  /**
   * Convenience: call mergedCustomerReceipts and download immediately.
   */
  async downloadMergedCustomerReceipts(ids = []) {
    const { blob, filename } = await this.mergedCustomerReceipts(ids, { dl: true });
    asDownload(blob, filename);
    return true;
  },

  // ---------- Export helpers ----------
  exportHref(params = {}) {
    return buildExportURL(params.fmt || 'csv', params);
  },
  exportCsvHref(params = {}) {
    return buildExportURL('csv', params);
  },
  exportXlsxHref(params = {}) {
    return buildExportURL('xlsx', params);
  },
};

// ---- Tanda Masuk sub-API ----
OrdersAPI.TandaMasuk = {
  async list() {
    const res = await fetch(`${API_BASE}/api/v3/orders/tanda_masuk`, {
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    return handleRes(res);
  },

  async show(id) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}/tanda_masuk`, {
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    return handleRes(res);
  },

  async destroy(id) {
    const res = await fetch(`${API_BASE}/api/v3/orders/${id}/tanda_masuk`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    if (!res.ok) throw new Error(`Failed to delete Tanda Masuk for order #${id}`);
    return true;
  },
};

export default OrdersAPI;
export { unwrap };
